import { Auth0Client } from '@auth0/auth0-spa-js'
import apiClient from '@/auth/apiClient'
import settingsClient from '@/settings/apiClient'
import customLabelClient from '@/custom-labels/apiClient'
import sentimentRulesClient from '@/sentiment-rules/apiClient'
import projectsClient from '@/projects/apiClient'
import overviewClient from '@/overview/apiClient'
import adminClient from '@/admin/apiClient'

/**
 * @type {Auth0Client}
 */
let auth0 = null

/**
 * @param {boolean} forceReInit
 */
function getAuth0Client (forceReInit = false) {
  if (auth0 && !forceReInit) return auth0
  auth0 = new Auth0Client({
    domain: process.env.VUE_APP_AUTH0_DOMAIN,
    client_id: process.env.VUE_APP_AUTH0_CLIENT_ID,
    audience: process.env.VUE_APP_AUTH0_API_AUDIENCE,
    redirect_uri: process.env.VUE_APP_BASE_URL + '/callback',
    scope: 'openid profile email admin',
    useRefreshTokens: true
  })
}

export const actionNames = {
  GET_LOGGED_IN_USER: 'getLoggedInUser',
  GET_ANONYMOUS_USER: 'getAnonymousUser',
  INIT_USER: 'initUser',
  LOGIN: 'login',
  HANDLE_AUTH_CALLBACK: 'handleAuthCallback',
  LOGOUT: 'logout',
  ACTIVATE: 'activate',
  INIT_GROUP_DATA: 'initGroupData'
}

export const module = {
  state: {
    isAuthenticated: false,
    accessToken: '',
    userId: '',
    clientId: 0,
    quota: 0,
    permission: null,
    subscription: {}
  },
  getters: {
    isAdmin (state) {
      return state.permission && (state.permission === 'Admin' || state.permission.includes('Admin'))
    }
  },
  mutations: {
    setIsAuthenticated (state, isAuthenticated) {
      state.isAuthenticated = isAuthenticated
    },
    setAccessToken (state, accessToken) {
      state.accessToken = accessToken
      customLabelClient.setAccessToken(accessToken)
      sentimentRulesClient.setAccessToken(accessToken)
      settingsClient.setAccessToken(accessToken)
      adminClient.setAccessToken(accessToken)
      overviewClient.setAccessToken(accessToken)
      projectsClient.setAccessToken(accessToken)
    },
    setUser (state, auth0User) {
      state.name = auth0User.name
      state.userId = auth0User.sub
      state.clientId = auth0User['https://textanalysis.neticle.com/client_id']
      state.quota = auth0User['https://textanalysis.neticle.com/quota']
      state.permission = auth0User['https://textanalysis.neticle.com/permission']
    },
    logout (state) {
      state.accessToken = undefined
      state.idToken = undefined
      state.expiresAt = undefined
      state.clientId = undefined
      state.quota = undefined
    }
  },
  actions: {
    async [actionNames.GET_ANONYMOUS_USER] () {
      getAuth0Client()
      if (await auth0.isAuthenticated()) {
        window.location = window.location.origin
      }
    },
    async [actionNames.GET_LOGGED_IN_USER] ({ state, commit, dispatch }) {
      getAuth0Client()
      try {
        await dispatch(actionNames.INIT_USER)
        commit('setIsAuthenticated', await auth0.isAuthenticated())
        if (!state.isAuthenticated) {
          await auth0.loginWithRedirect()
        }
      } catch (e) {
        await auth0.loginWithRedirect()
      }
    },
    async [actionNames.INIT_USER] ({ commit, state }, force = false) {
      getAuth0Client(force)
      commit('setAccessToken', await auth0.getTokenSilently({ ignoreCache: true }))
      console.warn('get token silently after.')
      const user = await auth0.getUser()
      commit('setUser', user)
    },
    async [actionNames.ACTIVATE] ({ state, commit }, { firstName, lastName, password, token }) {
      await apiClient.activate(firstName, lastName, password, token)
      window.location = window.location.origin
    },
    async [actionNames.HANDLE_AUTH_CALLBACK] ({ commit, dispatch }) {
      try {
        getAuth0Client()
        await auth0.handleRedirectCallback()
        await dispatch(actionNames.INIT_USER, true)
        window.location = window.location.origin
      } catch (e) {
        console.error('Handling Auth0 callback failed: ' + e.message)
        if (auth0) {
          dispatch(actionNames.LOGOUT)
        }
      }
    },
    [actionNames.LOGOUT] ({ commit }) {
      commit('logout')
      getAuth0Client()
      auth0.logout({
        returnTo: process.env.VUE_APP_BASE_URL,
        clientID: process.env.VUE_APP_AUTH0_CLIENT_ID
      })
    },
    async [actionNames.INIT_GROUP_DATA] ({ state }) {
      state.subscription = await settingsClient.getSubscription(state.clientId)
    }
  }
}
